/**
 * Peças compartilhadas entre o artigo interno (/knowledge/$id) e o público
 * (/kb/$slug): corpo HTML sanitizado com tipografia manual, cards de anexo
 * com download via blob e a caixa "Este artigo foi útil?".
 */
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { Download, Paperclip, ThumbsDown, ThumbsUp } from 'lucide-react';
import type { KnowledgeFile } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { downloadKnowledgeFile } from '@/features/knowledge/knowledge-mutations';
import { formatFileSize } from '@/features/knowledge/knowledge-format';

/**
 * Tipografia manual para HTML rico (o plugin @tailwindcss/typography não está
 * instalado — estilizamos os elementos comuns direto no container).
 */
export const RICH_TEXT_CLASSES =
  'text-sm leading-relaxed break-words ' +
  '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 ' +
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-0.5 ' +
  '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-primary-pressed ' +
  '[&_strong]:font-semibold [&_em]:italic [&_s]:line-through ' +
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-semibold ' +
  '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold ' +
  '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold ' +
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground ' +
  '[&_code]:rounded-sm [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[13px] ' +
  '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 ' +
  '[&_hr]:my-4 [&_img]:max-w-full';

/** Corpo HTML SANITIZADO (DOMPurify) com os estilos de tipografia manuais. */
export function KnowledgeHtml({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={cn(RICH_TEXT_CLASSES, className)}
      // eslint-disable-next-line react/no-danger -- sanitizado logo acima
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

/** Cards de anexo clicáveis — download via fetch+blob (bearer no header). */
export function KnowledgeAttachments({ files }: { files: KnowledgeFile[] }) {
  const [error, setError] = useState<string | null>(null);
  if (files.length === 0) return null;

  const download = (file: KnowledgeFile) => {
    setError(null);
    downloadKnowledgeFile(file).catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'Falha no download')
    );
  };

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold">Anexos ({files.length})</h2>
      <div className="flex flex-col gap-2">
        {files.map((file) => (
          <button
            key={file.id}
            type="button"
            onClick={() => download(file)}
            title={`Baixar ${file.fileName}`}
            className="group flex items-center gap-2.5 rounded-md border bg-card px-3 py-2 text-left transition-colors hover:border-primary/50 hover:bg-accent"
          >
            <Paperclip className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{file.fileName}</span>
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {formatFileSize(file.size)}
            </span>
            <Download className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
          </button>
        ))}
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </section>
  );
}

/**
 * Caixa cinza de feedback. `myVote` null = ainda não votou; votar de novo
 * troca o voto. Contagens são opcionais (a página pública não as tem).
 */
export function FeedbackBox({
  myVote,
  counts,
  pending,
  onVote,
}: {
  myVote: boolean | null;
  counts?: { yes: number; no: number };
  pending?: boolean;
  onVote: (helpful: boolean) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg bg-muted px-4 py-5 text-center">
      <p className="text-sm font-medium">
        {myVote === null ? 'Este artigo foi útil para você?' : 'Obrigado pelo feedback!'}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pending}
          aria-pressed={myVote === true}
          onClick={() => onVote(true)}
          className={cn(
            'text-primary hover:text-primary-pressed',
            myVote === true && 'border-primary bg-secondary text-secondary-foreground'
          )}
        >
          <ThumbsUp className="size-3.5" />
          Sim
          {counts ? <span className="tabular-nums">({counts.yes})</span> : null}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={pending}
          aria-pressed={myVote === false}
          onClick={() => onVote(false)}
          className={cn(
            'text-primary hover:text-primary-pressed',
            myVote === false && 'border-primary bg-secondary text-secondary-foreground'
          )}
        >
          <ThumbsDown className="size-3.5" />
          Não
          {counts ? <span className="tabular-nums">({counts.no})</span> : null}
        </Button>
      </div>
      {myVote !== null ? (
        <p className="text-xs text-muted-foreground">Você pode trocar o voto quando quiser.</p>
      ) : null}
    </div>
  );
}
